import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdCheckboxGroup,
  CmdInput,
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSelect,
  CmdSimpleSuggestion,
} from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import { debounce } from 'lodash';
import FormikValidatorService from '../../Services/FormikValidatorService';
import { IOption } from '../Forms/CmdField';
import { transformErrors } from '../../Services/HookFormService';
import useAnalytics from '../../hooks/useAnalytics';
import { getRequest, putRequest } from '../../Services/HttpService';

interface IAlarmRecipientsInputForm {
  name: string;
  email: string;
  phone: string;
  notifications: string[];
  viaEmailPeriodicity: string;
  viaCallPeriodicity: string;
  viaPushPeriodicity: string;
}

interface IRecipientFormProps {
  closeCallback: () => void;
  responseCallback: (values: any) => void;
  editCallback: (values: any) => void;
  editingRecipient: any;
  putRecipients: any;
}

interface IRecipient {
  userId: number;
  fullname: string;
  item: string;
  value: string;
}

export default function RecipientForm(props: IRecipientFormProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const defaultGroupOptions = [
    {
      value: 'map',
      item: f({ id: 'alarms.section.recipient.map' }),
      disabled: !(props.editingRecipient && props.editingRecipient.userId),
    },
    {
      value: 'ride',
      item: f({ id: 'alarms.section.recipient.ride' }),
      disabled: !(props.editingRecipient && props.editingRecipient.userId),
    },
    {
      value: 'email',
      item: f({ id: 'alarms.section.recipient.email' }),
      disabled: !(props.editingRecipient && props.editingRecipient.email),
    },
    {
      value: 'phone',
      item: f({ id: 'alarms.section.recipient.ring' }),
      disabled: !(
        props.editingRecipient &&
        (props.editingRecipient.phone || props.editingRecipient.telephone)
      ),
    },
    {
      value: 'push',
      item: f({ id: 'alarms.section.recipient.push' }),
      disabled: !(
        props.editingRecipient &&
        (props.editingRecipient.phone || props.editingRecipient.telephone)
      ),
    },
  ];
  const [validation, setValidation] = React.useState<any>(null);
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  // const [isUnknownUser, setIsUnknownUser] = React.useState<boolean | null>(true);
  const [isNextForm, setIsNextForm] = React.useState<boolean>(false);
  const [userOptions, setUserOptions] = React.useState<IRecipient[]>([]);
  const [allUsersOptions, setAllUsersOptions] = React.useState<any[]>([]);
  const [formReset, setFormReset] = React.useState(false);
  const [groupOptions, setGroupOptions] = React.useState<IOption[]>(defaultGroupOptions);
  const { trackEvent } = useAnalytics();

  const getValidation = async () => {
    setIsLoaderActive(true);
    const response = await getRequest('/v1/alarm/validation/recipient');
    if (response) {
      setValidation(response);
    }
    setIsLoaderActive(false);
  };

  useQueries({
    queries: [
      {
        queryKey: ['recipientFormValidation'],
        queryFn: () => getValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const changeGroupOptions = (emailDisabled: boolean, phoneDisabled: boolean) => {
    setGroupOptions((oldState) => {
      return oldState.map((item) => {
        if (item.value === 'email') {
          return { ...item, disabled: emailDisabled };
        }
        if (item.value === 'phone') {
          return { ...item, disabled: phoneDisabled };
        }
        if (item.value === 'push') {
          return { ...item, disabled: phoneDisabled };
        }
        return item;
      });
    });
  };

  const validateFields = (values: any) => {
    let formErrors: any = {};
    if (!validation) {
      return formErrors;
    }
    delete validation.name;
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, validation);
    if (!values.phone && !values.email) {
      formErrors.email = f({ id: 'alarms.section.recipient.error' });
    }
    const err: Record<string, string> = transformErrors(formErrors);
    changeGroupOptions(!values.email || !!err.email, !values.phone || !!err.phone);
    return err;
  };

  const getCheckedNotifications = () => {
    const recipient = props.editingRecipient;
    const values: string[] = [];
    if (recipient.viaCall) {
      values.push('phone');
    }
    if (recipient.viaPush) {
      values.push('push');
    }
    if (recipient.viaEmail) {
      values.push('email');
    }
    if (recipient.viaMap) {
      values.push('map');
    }
    if (recipient.viaRide) {
      values.push('ride');
    }
    return values;
  };

  const {
    handleSubmit,
    watch,
    control,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm<IAlarmRecipientsInputForm>({
    mode: 'onChange',
    defaultValues: {
      name: props.editingRecipient ? props.editingRecipient.userId : '',
      email: props.editingRecipient ? props.editingRecipient.email : '',
      phone: props.editingRecipient
        ? props.editingRecipient.phone || props.editingRecipient.telephone
        : '',
      notifications: props.editingRecipient ? getCheckedNotifications() : [],
      viaEmailPeriodicity: props.editingRecipient?.viaEmailPeriodicity || 'once_per_day',
      viaCallPeriodicity: props.editingRecipient?.viaCallPeriodicity || 'once_per_day',
      viaPushPeriodicity: props.editingRecipient?.viaPushPeriodicity || 'once_per_day',
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const getFrequencyOptions = () => {
    return [
      {
        item: f({ id: 'alarms.section.recipient.select.daily' }),
        value: 'once_per_day',
        tooltip: f({ id: 'alarms.section.recipient.select.daily.tooltip' }),
      },
      {
        item: f({ id: 'alarms.section.recipient.select.always' }),
        value: 'each_start',
        tooltip: f({ id: 'alarms.section.recipient.select.always.tooltip' }),
      },
    ];
  };

  const getTooltipText = (value: string) => {
    if (value === 'each_start') {
      return f({ id: 'alarms.section.recipient.select.always.tooltip' });
    }
    if (value === 'once_per_day') {
      return f({ id: 'alarms.section.recipient.select.daily.tooltip' });
    }
    return '';
  };

  const onSubmit = (values: any) => {
    if (values.phone || values.email) {
      const newValues = { ...values };
      newValues.viaMap = false;
      newValues.viaRide = false;
      newValues.viaEmail = false;
      newValues.viaCall = false;
      newValues.viaPush = false;

      if (allUsersOptions.length > 0 && values.name) {
        const user = allUsersOptions.find((i: any) => String(i.userId) === String(values.name));
        if (user) {
          newValues.userId = user.userId;
          newValues.name = user.fullname;
          newValues.fullname = user.fullname;
        }
      }

      if (Number.isInteger(Number(values.name))) {
        newValues.name = '';
      }

      if (props.editingRecipient) {
        newValues.key = props.editingRecipient.key;
        newValues.fullname = props.editingRecipient.fullname || newValues.name;
        newValues.userId = props.editingRecipient.userId || newValues.userId || null;
        newValues.name = newValues.fullname;
      }

      if (values.notifications) {
        values.notifications.forEach((item: string) => {
          if (item === 'map') {
            newValues.viaMap = !!newValues.userId;
          }
          if (item === 'ride') {
            newValues.viaRide = !!newValues.userId;
          }
          if (item === 'email') {
            newValues.viaEmail = !!newValues.email;
          }
          if (item === 'phone') {
            newValues.viaCall = !!newValues.phone;
          }
          if (item === 'push') {
            newValues.viaPush = !!newValues.phone;
          }
        });
        delete newValues.notifications;
      }
      if (!newValues.viaEmail) {
        newValues.viaEmailPeriodicity = '';
      }
      if (!newValues.viaCall) {
        newValues.viaCallPeriodicity = '';
      }
      if (!newValues.viaPush) {
        newValues.viaPushPeriodicity = '';
      }

      if (props.editingRecipient) {
        props.editCallback(newValues);
      } else {
        props.responseCallback(newValues);
      }

      if (isNextForm) {
        reset();
        setFormReset(true);
        setAllUsersOptions([]);
        setUserOptions([]);
        setGroupOptions(defaultGroupOptions);
        setIsNextForm(false);
      } else {
        props.closeCallback();
      }
    }
  };

  const getNameOptions = async (val: string) => {
    const inputs = {
      query: val,
      recipientUserId: [],
    };
    const res = await putRequest('/v1/alarm/form/recipient', inputs);
    if (res) {
      const putRecipientNames = props.putRecipients.map(
        (item: { email: string; fullname: string }) => {
          return item.fullname;
        }
      );
      const recipientArr = res.recipients.filter(
        (item: IRecipient) => !putRecipientNames.includes(item.fullname)
      );
      setUserOptions(recipientArr);
      setAllUsersOptions(recipientArr);
    }
  };

  const debouncedNameOptions = debounce((value: string) => {
    const regexName = /^([ a-zA-Z]){2,}[ a-zA-Z]*$/i;
    if (value && value.length < 36 && regexName.test(value)) {
      getNameOptions(value);
    } else {
      setUserOptions([]);
    }
  }, 900);

  const handleRecipientsOptions = (event: any) => {
    if (event && event.target) {
      trackEvent(['alarm', 'change', 'Alarm Recipient options changed']);
      const val = event.target.value;
      debouncedNameOptions(val);
    }
  };

  const handleChosenRecipient = (value: string | number) => {
    const inputs = allUsersOptions.find((i) => String(i.userId) === String(value));
    trackEvent(['alarm', 'change', 'Alarm Recipient chosen']);
    let isNotEmail: boolean | null = true;
    let isNotPhoneNumber: boolean | null = true;
    let isNotMap: boolean | null = true;
    let isNotRide: boolean | null = true;
    if (inputs) {
      isNotMap = null;
      isNotRide = null;
      if (inputs.email) {
        isNotEmail = null;
      }
      if (inputs.phone) {
        isNotPhoneNumber = null;
      }
      Object.keys(inputs).map((indexId: string) => {
        if (indexId === 'userId') {
          setValue('name', inputs.userId);
        } else if (indexId === 'email') {
          setValue('email', inputs.email || '');
        } else if (indexId === 'phone') {
          setValue('phone', inputs.phone || '');
        }
      });
      // setIsUnknownUser(null);
    } else {
      setValue('name', String(value));
      // setIsUnknownUser(true);
      isNotEmail = !watch('email');
      isNotPhoneNumber = !watch('phone');
    }
    setFormReset(false);
    const checkboxgroupOptions = {
      map: isNotMap,
      ride: isNotRide,
      email: isNotEmail,
      phone: isNotPhoneNumber,
      push: isNotPhoneNumber,
    };
    setGroupOptions((oldState) => {
      return oldState.map((item) => {
        return { ...item, disabled: checkboxgroupOptions[item.value] };
      });
    });
    trigger('email');
    trigger('phone');
  };

  const handleNextForm = (value: boolean) => {
    setIsNextForm(value);
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.rule.button.close' }),
      tooltip: f({ id: 'alarms.modal.recipient.close.tooltip' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'alarm-recipient-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'alarms.modal.recipient.save' }),
      tooltip: f({ id: 'alarms.modal.recipient.save.tooltip' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];

  return (
    <CmdModal
      id="recipient-form-modal"
      title={
        props.editingRecipient
          ? f({ id: 'alarms.modal.recipient.edit' })
          : f({ id: 'alarms.modal.recipient.title' })
      }
      footerButtons={buttons}
      loader={isLoaderActive}
      modalFooterCheckbox={
        props.editingRecipient ? '' : f({ id: 'alarms.section.recipient.nextForm' })
      }
      modalFooterCheckboxReset={formReset}
      modalFooterCheckboxTitle={f({ id: 'alarms.section.recipient.nextForm.tooltip' })}
      modalFooterCheckboxCallback={handleNextForm}
    >
      <div>
        {props.editingRecipient &&
        (props.editingRecipient.userId ||
          props.editingRecipient.name ||
          props.editingRecipient.fullname) ? (
          <CmdLabel
            title={f({ id: 'alarms.section.recipient.name' })}
            value={props.editingRecipient.fullname || props.editingRecipient.name}
          />
        ) : (
          <Controller
            name="name"
            control={control}
            rules={{ required: errors.name ? errors.name.message : undefined }}
            render={({ field }) => (
              <CmdSimpleSuggestion
                {...field}
                // control={control}
                label={f({ id: 'alarms.section.recipient.name' })}
                id="name"
                placeholder={f({ id: 'form.search' })}
                options={userOptions.map((i) => {
                  return { value: String(i.userId), item: i.fullname };
                })}
                required={false}
                onChangeCallback={handleRecipientsOptions}
                onSubmitCallback={handleChosenRecipient}
                setValue={(value) => {
                  setValue('name', value);
                  trigger('name');
                }}
                introOption={f({ id: 'form.choose' })}
                tooltip={f({ id: 'alarms.modal.recipient.formField.recipient.tooltip' })}
                reset={formReset}
              />
            )}
          />
        )}
        <Controller
          name="email"
          control={control}
          rules={{ required: errors.email ? errors.email.message : undefined }}
          render={({ field }) => (
            <CmdInput
              {...field}
              label={f({ id: 'alarms.section.recipient.email' })}
              id="email"
              name="email"
              type="text"
              required={true}
              onChangeCallback={(value) => {
                setValue('email', value);
                trigger('email');
              }}
              tooltip={f({ id: 'alarms.modal.recipient.formField.email.tooltip' })}
              error={errors.email?.message}
              touched={true}
            />
          )}
        />
        <Controller
          name="phone"
          control={control}
          rules={{ required: errors.phone ? errors.phone.message : undefined }}
          render={({ field }) => (
            <CmdInput
              {...field}
              label={f({ id: 'alarms.section.recipient.cellphone' })}
              id="phone"
              name="phone"
              type="text"
              placeholder="+421XXX"
              required={false}
              onChangeCallback={(value) => {
                setValue('phone', value);
                trigger('phone');
                trigger('email');
              }}
              tooltip={f({ id: 'alarms.modal.recipient.formField.phone.tooltip' })}
              error={errors.phone?.message}
              touched={true}
            />
          )}
        />
        <Controller
          name="notifications"
          control={control}
          rules={{
            required: errors.notifications ? errors.notifications.message : undefined,
          }}
          render={({ field }) => (
            <CmdCheckboxGroup
              {...field}
              id="notifications"
              name="notifications"
              label=""
              options={groupOptions}
              tooltip={f({ id: 'alarms.modal.recipient.formField.alertType.tooltip' })}
              setValue={(value: any) => {
                setValue('notifications', value);
                trigger('notifications');
              }}
            />
          )}
        />
      </div>
      {((watch('email') && watch('notifications').includes('email')) ||
        (watch('phone') &&
          (watch('notifications').includes('phone') ||
            watch('notifications').includes('push')))) && (
        <>
          <div className="form-group-separator">
            {f({ id: 'alarms.section.recipients.subtitle' })}
          </div>
          <div className="p-6">
            {watch('email') && watch('notifications').includes('email') && (
              <Controller
                name="viaEmailPeriodicity"
                control={control}
                rules={{
                  required: errors.viaEmailPeriodicity
                    ? errors.viaEmailPeriodicity.message
                    : undefined,
                }}
                render={({ field }) => (
                  <CmdSimpleSelect
                    {...field}
                    id="viaEmailPeriodicity"
                    name="viaEmailPeriodicity"
                    label={f({ id: 'alarms.section.recipient.email' })}
                    options={getFrequencyOptions()}
                    tooltip={
                      watch('viaEmailPeriodicity')
                        ? getTooltipText(watch('viaEmailPeriodicity'))
                        : f({ id: 'alarms.modal.recipient.formField.emailFrequency.tooltip' })
                    }
                    onChangeCallback={(value: string) => {
                      setValue('viaEmailPeriodicity', value);
                      trackEvent(['alarm', 'change', 'Alarm Recipient email frequency changed']);
                    }}
                  />
                )}
              />
            )}
            {watch('phone') && watch('notifications').includes('phone') && (
              <Controller
                name="viaCallPeriodicity"
                control={control}
                rules={{
                  required: errors.viaCallPeriodicity
                    ? errors.viaCallPeriodicity.message
                    : undefined,
                }}
                render={({ field }) => (
                  <CmdSimpleSelect
                    {...field}
                    id="viaCallPeriodicity"
                    name="viaCallPeriodicity"
                    label={f({ id: 'alarms.section.recipient.ring' })}
                    options={getFrequencyOptions()}
                    tooltip={
                      watch('viaCallPeriodicity')
                        ? getTooltipText(watch('viaCallPeriodicity'))
                        : f({ id: 'alarms.modal.recipient.formField.ringFrequency.tooltip' })
                    }
                    onChangeCallback={(value: string) => {
                      setValue('viaCallPeriodicity', value);
                      trackEvent(['alarm', 'change', 'Alarm Recipient ring frequency changed']);
                    }}
                  />
                )}
              />
            )}
            {watch('phone') && watch('notifications').includes('push') && (
              <Controller
                name="viaPushPeriodicity"
                control={control}
                rules={{
                  required: errors.viaPushPeriodicity
                    ? errors.viaPushPeriodicity.message
                    : undefined,
                }}
                render={({ field }) => (
                  <CmdSimpleSelect
                    {...field}
                    id="viaPushPeriodicity"
                    name="viaPushPeriodicity"
                    label={f({ id: 'alarms.section.recipient.push' })}
                    options={getFrequencyOptions()}
                    tooltip={
                      watch('viaPushPeriodicity')
                        ? getTooltipText(watch('viaPushPeriodicity'))
                        : f({ id: 'alarms.modal.recipient.formField.pushFrequency.tooltip' })
                    }
                    onChangeCallback={(value: string) => {
                      setValue('viaPushPeriodicity', value);
                      trackEvent(['alarm', 'change', 'Alarm Recipient push frequency changed']);
                    }}
                  />
                )}
              />
            )}
          </div>
        </>
      )}
    </CmdModal>
  );
}
